import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, Settings, User, Mail, Phone, MessageSquare, Send } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-hot-toast';
import { addDocument } from '../../lib/firestore-helpers';
import { notifyRequest } from '../../lib/notify';

interface TireConfig {
  type: string;
  brand: string;
  season: string;
  quantity: string;
  width: string;
  height: string;
  diameter: string;
  loadIndex: string;
  speedIndex: string;
  runflat: boolean;
  xl: boolean;
  reseller: boolean;
}

interface QuoteFormData {
  fullName: string;
  email: string;
  phone: string;
  message: string;
}

interface TireQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: TireConfig;
}

const quoteSchema = yup.object().shape({
  fullName: yup.string().required('Nom complet requis'),
  email: yup.string().email('Email invalide').required('Email requis'),
  phone: yup.string().required('Téléphone requis'),
  message: yup.string()
});

const TireQuoteModal: React.FC<TireQuoteModalProps> = ({ isOpen, onClose, config }) => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<QuoteFormData>({ 
    resolver: yupResolver(quoteSchema) 
  });

  const getTireConfigSummary = () => {
    const parts = [];
    
    if (config.width && config.height && config.diameter) {
      parts.push(`${config.width}/${config.height}R${config.diameter}`);
    }
    if (config.loadIndex && config.speedIndex) {
      parts.push(`${config.loadIndex}${config.speedIndex}`);
    }
    
    const details = [];
    details.push(`Type: ${config.type}`);
    details.push(`Marque: ${config.brand}`);
    details.push(`Saison: ${config.season}`);
    details.push(`Quantité: ${config.quantity} pneu${config.quantity !== '1' ? 's' : ''}`);
    
    if (config.runflat) details.push('RUNFLAT');
    if (config.xl) details.push('XL Renforcé');
    if (config.reseller) details.push('Revendeur uniquement');
    
    return {
      size: parts.join(' '),
      details: details.join(' • ')
    };
  };

  const onSubmit = async (data: QuoteFormData) => {
    setIsSubmitting(true);
    
    try {
      const requestData = { ...data, tireConfig: config } as unknown as Record<string, unknown>;
      await addDocument('requests', {
        type: 'quote' as const,
        data: requestData,
        status: 'new' as const,
        notes: '',
      });

      notifyRequest('quote', { ...data, tireSummary: getTireConfigSummary().size + ' - ' + getTireConfigSummary().details });
      toast.success('Votre demande de devis a ete envoyee !');
      reset();
      onClose();

    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez reessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    document.body.style.overflow = 'unset';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white">{t('tires.quote.title')}</h3>
              <motion.button
                onClick={handleClose}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 bg-white/20 rounded-lg text-white hover:bg-white/30 transition-colors"
              >
                <X className="w-6 h-6" />
              </motion.button>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
              {/* Tire Configuration Summary */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('tires.quote.selectedConfig')}</h4>
                <div className="space-y-2">
                  {getTireConfigSummary().size && (
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                        <Settings className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xl font-bold text-gray-900">{getTireConfigSummary().size}</span>
                    </div>
                  )}
                  <p className="text-gray-600 ml-11">{getTireConfigSummary().details}</p>
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tires.quote.form.fullName')} *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      {...register('fullName')}
                      className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        errors.fullName ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                      }`}
                      placeholder={t('tires.quote.form.fullNamePlaceholder')}
                    />
                  </div>
                  {errors.fullName && (
                    <p className="mt-2 text-sm text-red-600">{t('tires.quote.validation.nameRequired')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tires.quote.form.phone')} *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="tel"
                      {...register('phone')}
                      className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        errors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                      }`}
                      placeholder={t('tires.quote.form.phonePlaceholder')}
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-2 text-sm text-red-600">{t('tires.quote.validation.phoneRequired')}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tires.quote.form.email')} *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    {...register('email')}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                    placeholder={t('tires.quote.form.emailPlaceholder')}
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.email.type === 'required' 
                      ? t('tires.quote.validation.emailRequired')
                      : t('tires.quote.validation.emailInvalid')
                    }
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tires.quote.form.message')}
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <textarea
                    {...register('message')}
                    rows={4}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none transition-colors resize-none"
                    placeholder={t('tires.quote.form.messagePlaceholder')}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={!isSubmitting ? { scale: 1.02, y: -2 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                className={`w-full flex items-center justify-center space-x-3 py-4 font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl ${
                  isSubmitting
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full"
                    />
                    <span>{t('tires.quote.form.submitting')}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>{t('tires.quote.form.submit')}</span>
                  </>
                )}
              </motion.button>

              <p className="text-center text-sm text-gray-500">
                {t('tires.quote.form.footer')}
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TireQuoteModal;